import type { IdOrIdentifier } from "@suse-o11y/id-or-identifier";
import React from "react";

import type { DashboardSpec } from "../../../common/dashboard-spec";
import { ErrorMessage } from "../../../components/_notification-error";
import { DONT_AUTO_DISMISS, notification } from "../../../components/react/notification/Notification";
import { useOptimisticUpdateDashboard } from "../../../queries/dashboards";

export interface UseSaveDashboardParams {
	idOrIdentifier: IdOrIdentifier;
	revision: undefined | DashboardSpec;
}

export function useSaveDashboard({
	onError,
}: {
	onError?: (error: Error, params: UseSaveDashboardParams) => void;
} = {}) {
	const { mutate } = useOptimisticUpdateDashboard({
		onSuccess: ({ name }) => {
			notification.success({
				message: `The dashboard "${name}" has been successfully saved.`,
			});
		},

		onError(error, { idOrIdentifier, diff }) {
			if (onError) {
				onError(error, { idOrIdentifier, revision: diff.spec });
			} else {
				notification.error({
					duration: DONT_AUTO_DISMISS,
					message: <ErrorMessage error={error}>Something went wrong while trying to save the dashboard.</ErrorMessage>,
				});
			}
		},
	});

	return ({ idOrIdentifier, revision }: UseSaveDashboardParams): void => {
		mutate({
			idOrIdentifier,
			diff: { spec: revision },
		});
	};
}
